import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, MailCheck } from "lucide-react";

/**
 * Forgot-password modal.
 *
 * UX: collect email → POST /auth/forgot-password → show "check your inbox"
 * confirmation regardless of whether the email actually exists (the backend
 * is intentionally non-revealing to prevent account enumeration).
 *
 * The only exception is when the user signed up via Google — backend returns
 * a 400 with a clear message asking them to use "Continue with Google",
 * which we surface as a toast so they don't leave confused.
 */
export default function ForgotPasswordModal({ open, onOpenChange, defaultEmail = "" }) {
  const [email, setEmail] = useState(defaultEmail);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (error) {
      // Surface only the explicit Google-account guidance; everything else
      // we treat as success to maintain non-enumeration guarantee.
      const detail = error.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("google")) {
        toast.error(detail);
      } else {
        // Treat other errors as success so we don't leak existence info
        setSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (next) => {
    onOpenChange(next);
    // Reset for next open
    if (!next) {
      setTimeout(() => {
        setSent(false);
        setEmail(defaultEmail);
      }, 200);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="forgot-password-modal">
        {!sent ? (
          <>
            <DialogHeader>
              <DialogTitle>Forgot your password?</DialogTitle>
              <DialogDescription>
                Enter your email address and we'll send you a link to reset your password.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                data-testid="forgot-email-input"
                autoFocus
                autoComplete="email"
              />
              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full h-11 rounded-lg bg-gray-900 hover:bg-gray-800 text-white font-medium disabled:bg-gray-300 disabled:text-gray-500"
                data-testid="forgot-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="forgot-confirmation">
            <div className="mx-auto w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center mb-4">
              <MailCheck className="w-6 h-6 text-teal-600" />
            </div>
            <DialogTitle className="text-lg">Check your inbox</DialogTitle>
            <p className="text-sm text-gray-500 mt-2 leading-relaxed">
              If an account exists for <span className="font-medium text-gray-700">{email}</span>,
              we've sent a password reset link. The link expires in 30 minutes.
            </p>
            <p className="text-xs text-gray-400 mt-4">
              Didn't get the email? Check your spam folder.
            </p>
            <Button
              variant="ghost"
              onClick={() => handleClose(false)}
              className="mt-5 text-sm"
              data-testid="forgot-close-btn"
            >
              Got it
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
